import {
  adminBracketSchema,
  minForGroups,
  seedingSchema,
  type AdminBracket,
  type AdminTournament,
  type Bracket,
  type BracketMatch,
  type Seeding,
} from '@growup/site-shared';
import {
  BracketView,
  type BracketViewMatch,
  GroupStage,
  Button,
  Card,
  DateTimeInput,
  Checkbox,
  Field,
  Input,
  Notice,
  Select,
} from '@growup/ui';
import { useCallback, useEffect, useState } from 'react';

import { getJson, messageOf, send } from '../api';
import { isoToLisbonInput, lisbonInputToIso } from '../events/labels';
import { formatDateTime } from '../format';

const SEEDING_LABEL: Record<Seeding, string> = {
  random: 'Ao acaso',
  registration: 'Pela ordem de inscrição',
  manual: 'À mão (ordenar a lista)',
};

const matchTime = new Intl.DateTimeFormat('pt-PT', {
  timeZone: 'Europe/Lisbon',
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

export const matchView = (m: BracketMatch): BracketViewMatch => ({
  id: m.id,
  a: m.a,
  b: m.b,
  winner: m.winner,
  bye: m.bye,
  scoreA: m.scoreA,
  scoreB: m.scoreB,
  note: m.forfeit ? 'W.O.' : m.draw ? 'Empate' : null,
  when: m.scheduledAt === null ? null : matchTime.format(new Date(m.scheduledAt)),
});

const groupMatches = (b: Bracket) => b.groups.flatMap((g) => g.rounds.flatMap((r) => r.matches));
const playoffMatches = (b: Bracket) => [
  ...b.rounds.flatMap((r) => r.matches),
  ...(b.thirdPlace ? [b.thirdPlace] : []),
];
const hasResult = (m: BracketMatch) => !m.bye && (m.winner !== null || m.draw);

const allMatches = (b: Bracket) => [
  ...groupMatches(b),
  ...b.rounds.flatMap((r) => r.matches),
  ...(b.thirdPlace ? [b.thirdPlace] : []),
];

export function bracketRounds(bracket: Bracket) {
  return bracket.rounds.map((r) => ({ title: r.title, matches: r.matches.map(matchView) }));
}

/**
 * O bracket (parte 2): quem joga, cabeças de série, gerar e refazer. Gerar
 * fecha as inscrições e o check-in.
 */
export function BracketCard({
  tournament,
  onChanged,
}: {
  tournament: AdminTournament;
  onChanged: () => void;
}) {
  const [data, setData] = useState<AdminBracket | null>(null);
  const [seeding, setSeeding] = useState<Seeding>('random');
  const [order, setOrder] = useState<{ entryId: string; name: string }[]>([]);
  const [confirmReset, setConfirmReset] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const base = `/api/site/tournaments/${tournament.id}/bracket`;

  const load = useCallback(() => {
    getJson(base, adminBracketSchema)
      .then((d) => {
        setData(d);
        setOrder(d.eligible);
      })
      .catch((e: unknown) => setError(messageOf(e)));
  }, [base]);
  useEffect(load, [
    load,
    tournament.confirmed,
    tournament.waitlisted,
    tournament.checkedIn,
    tournament.bracketGeneratedAt,
    tournament.updatedAt,
  ]);

  const run = async (
    method: 'POST' | 'PUT' | 'DELETE',
    body?: unknown,
    path: string = base,
  ): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      const next = adminBracketSchema.parse(await send(method, path, body));
      setData(next);
      setOrder(next.eligible);
      setConfirmReset(false);
      onChanged();
      return true;
    } catch (e) {
      setError(messageOf(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const move = (index: number, by: -1 | 1) =>
    setOrder((current) => {
      const next = [...current];
      const [item] = next.splice(index, 1);
      if (item) next.splice(index + by, 0, item);
      return next;
    });

  if (!data) {
    return (
      <Card headingLevel={2} title="Bracket">
        {error ? <Notice tone="danger">{error}</Notice> : <p>A carregar…</p>}
      </Card>
    );
  }

  if (data.bracket) {
    const b = data.bracket;
    const match = allMatches(b).find((m) => m.id === editing) ?? null;
    const played = allMatches(b).some(hasResult);
    const groups = b.groups.length > 0;
    const inGroups = groupMatches(b).some((m) => m.id === editing);
    const groupsDone = groups && b.groups.every((g) => g.complete);
    const playoffsPlayed = playoffMatches(b).some(hasResult);
    return (
      <Card headingLevel={2} title="Bracket">
        <div className="flex flex-col gap-4">
          <p className="text-caption text-gu-ink-muted">
            Gerado a {formatDateTime(b.generatedAt)}. Está no site, na página do torneio. Carregue
            num jogo para pôr o resultado ou a hora.
          </p>
          <p className="text-caption text-gu-ink-muted">
            Vista TV/OBS para os streams: <code>growup.gg/torneios/{tournament.slug}/tv</code>{' '}
            (atualiza sozinha; para fundo transparente no OBS junte <code>?fundo=transparente</code>
            ).
          </p>
          {b.finished ? (
            <Notice tone="success" title="Terminado">
              {b.standings
                .slice(0, 3)
                .map((s) => `${String(s.rank)}.º ${s.name}`)
                .join(' · ')}
            </Notice>
          ) : null}
          {groups ? (
            <>
              <h3 className="font-sub text-label font-bold uppercase">Fase de grupos</h3>
              <GroupStage
                groups={b.groups.map((g) => ({
                  name: g.name,
                  standings: g.standings,
                  rounds: g.rounds.map((r) => ({
                    title: r.title,
                    matches: r.matches.map(matchView),
                  })),
                }))}
                onMatchClick={setEditing}
              />
              <h3 className="font-sub text-label font-bold uppercase">Playoffs</h3>
            </>
          ) : null}
          {b.rounds.length > 0 ? (
            <BracketView
              rounds={bracketRounds(b)}
              thirdPlace={b.thirdPlace ? matchView(b.thirdPlace) : null}
              onMatchClick={setEditing}
            />
          ) : null}
          {groups && b.rounds.length === 0 ? (
            <div className="flex flex-col gap-2">
              <p className="text-caption text-gu-ink-muted">
                {groupsDone
                  ? 'Os grupos acabaram. Confirme as tabelas e gere os playoffs: o 1.º de um grupo joga com o 2.º do outro.'
                  : 'Os playoffs geram-se quando todos os jogos dos grupos tiverem resultado.'}
              </p>
              <Button
                className="w-fit"
                disabled={busy || !groupsDone}
                onClick={() =>
                  void run('POST', undefined, `/api/site/tournaments/${tournament.id}/playoffs`)
                }
              >
                Gerar playoffs
              </Button>
            </div>
          ) : null}
          {groups && b.rounds.length > 0 && !playoffsPlayed ? (
            <Button
              size="sm"
              variant="secondary"
              className="w-fit"
              disabled={busy}
              onClick={() =>
                void run('DELETE', undefined, `/api/site/tournaments/${tournament.id}/playoffs`)
              }
            >
              Refazer playoffs (para corrigir um resultado dos grupos)
            </Button>
          ) : null}
          {match ? (
            <MatchEditor
              key={match.id}
              match={match}
              allowDraw={inGroups}
              busy={busy}
              onSave={async (body) => {
                if (
                  await run(
                    'PUT',
                    body,
                    `/api/site/tournaments/${tournament.id}/matches/${match.id}`,
                  )
                ) {
                  setEditing(null);
                }
              }}
              onSchedule={async (scheduledAt) => {
                await run(
                  'PUT',
                  { scheduledAt },
                  `/api/site/tournaments/${tournament.id}/matches/${match.id}/schedule`,
                );
              }}
              onClear={async () => {
                if (
                  await run(
                    'DELETE',
                    undefined,
                    `/api/site/tournaments/${tournament.id}/matches/${match.id}`,
                  )
                ) {
                  setEditing(null);
                }
              }}
              onClose={() => setEditing(null)}
            />
          ) : null}
          {played ? null : (
            <div className="flex flex-col gap-2 border-t border-gu-line pt-3">
              <Checkbox
                label="Confirmo: apagar o bracket e voltar às inscrições"
                checked={confirmReset}
                onChange={(e) => setConfirmReset(e.target.checked)}
              />
              <Button
                variant="secondary"
                className="w-fit"
                disabled={busy || !confirmReset}
                onClick={() => void run('DELETE')}
              >
                Refazer bracket
              </Button>
            </div>
          )}
          {error ? <Notice tone="danger">{error}</Notice> : null}
        </div>
      </Card>
    );
  }

  const n = data.eligible.length;
  const withGroups = tournament.format === 'groups_playoffs';
  const needed = withGroups ? minForGroups(tournament.groupCount, tournament.advancePerGroup) : 2;
  return (
    <Card headingLevel={2} title="Bracket">
      <div className="flex flex-col gap-4">
        <p className="text-gu-ink-muted">
          {n === 1 ? '1 participante entra' : `${String(n)} participantes entram`} no bracket
          {tournament.checkInMinutes !== null
            ? ' (quem fez check-in; os lugares de quem faltou vão para a lista de espera).'
            : ' (os confirmados).'}{' '}
          Ao gerar, as inscrições
          {tournament.checkInMinutes !== null ? ' e o check-in' : ''} fecham.
        </p>
        <Field label="Cabeças de série">
          <Select value={seeding} onChange={(e) => setSeeding(seedingSchema.parse(e.target.value))}>
            {seedingSchema.options.map((s) => (
              <option key={s} value={s}>
                {SEEDING_LABEL[s]}
              </option>
            ))}
          </Select>
        </Field>
        {seeding === 'manual' ? (
          <ol className="flex flex-col gap-1" aria-label="Ordem das cabeças de série">
            {order.map((e, i) => (
              <li key={e.entryId} className="flex items-center gap-2">
                <span className="w-6 text-right text-caption text-gu-ink-muted">{i + 1}</span>
                <span className="flex-1">{e.name}</span>
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={`Subir ${e.name}`}
                  disabled={i === 0}
                  onClick={() => move(i, -1)}
                >
                  ↑
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={`Descer ${e.name}`}
                  disabled={i === order.length - 1}
                  onClick={() => move(i, 1)}
                >
                  ↓
                </Button>
              </li>
            ))}
          </ol>
        ) : null}
        <p className="text-caption text-gu-ink-muted">
          {withGroups
            ? `As cabeças de série são distribuídas pelos ${String(tournament.groupCount)} grupos em serpentina (A, B, B, A...), para os grupos ficarem equilibrados.`
            : 'As cabeças de série mais altas só se encontram no fim e, se o número não for 4, 8, 16 ou 32, passam a 1.ª ronda sem jogar.'}
        </p>
        <div>
          <Button
            disabled={busy || n < needed}
            onClick={() =>
              void run('POST', {
                seeding,
                order: seeding === 'manual' ? order.map((e) => e.entryId) : null,
              })
            }
          >
            {withGroups ? 'Gerar grupos' : 'Gerar bracket'}
          </Button>
        </div>
        {n < needed ? (
          <p className="text-caption text-gu-ink-muted">
            São precisos pelo menos {needed} participantes.
          </p>
        ) : null}
        {error ? <Notice tone="danger">{error}</Notice> : null}
      </div>
    </Card>
  );
}

/** O resultado de um jogo: vitorias (ou golos, num melhor de 1) ou W.O. */
function MatchEditor({
  match,
  allowDraw,
  busy,
  onSave,
  onSchedule,
  onClear,
  onClose,
}: {
  match: BracketMatch;
  /** Jogo dos grupos: num jogo unico pode haver empate. */
  allowDraw: boolean;
  busy: boolean;
  onSave: (body: unknown) => Promise<void>;
  onSchedule: (scheduledAt: string | null) => Promise<void>;
  onClear: () => Promise<void>;
  onClose: () => void;
}) {
  const [scoreA, setScoreA] = useState(match.scoreA === null ? '' : String(match.scoreA));
  const [scoreB, setScoreB] = useState(match.scoreB === null ? '' : String(match.scoreB));
  const a = match.a?.name ?? '?';
  const b = match.b?.name ?? '?';
  const valid = /^\d+$/.test(scoreA.trim()) && /^\d+$/.test(scoreB.trim());
  const [when, setWhen] = useState(isoToLisbonInput(match.scheduledAt));
  const whenIso = lisbonInputToIso(when);
  return (
    <section
      aria-label={`Resultado de ${a} contra ${b}`}
      className="flex flex-col gap-3 rounded border border-gu-line p-3"
    >
      <p className="font-semibold">
        {a} contra {b}
        <span className="ml-2 text-caption font-normal text-gu-ink-muted">
          {match.bestOf === 1
            ? allowDraw
              ? 'Jogo único: o resultado (ex.: golos). Empate vale 1 ponto.'
              : 'Jogo único: o resultado (ex.: golos).'
            : `Melhor de ${String(match.bestOf)}: ganha quem chega a ${String(Math.ceil(match.bestOf / 2))}.`}
        </span>
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <Field label={a}>
          <Input
            inputMode="numeric"
            className="w-20"
            value={scoreA}
            onChange={(e) => setScoreA(e.target.value)}
          />
        </Field>
        <Field label={b}>
          <Input
            inputMode="numeric"
            className="w-20"
            value={scoreB}
            onChange={(e) => setScoreB(e.target.value)}
          />
        </Field>
        <Button
          disabled={busy || !valid}
          onClick={() => void onSave({ scoreA: Number(scoreA), scoreB: Number(scoreB) })}
        >
          Guardar resultado
        </Button>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="secondary"
          disabled={busy}
          onClick={() => void onSave({ forfeitWinner: 'a' })}
        >
          {a} ganha por falta
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={busy}
          onClick={() => void onSave({ forfeitWinner: 'b' })}
        >
          {b} ganha por falta
        </Button>
        {match.winner !== null || match.draw ? (
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => void onClear()}>
            Apagar resultado
          </Button>
        ) : null}
        <Button size="sm" variant="ghost" onClick={onClose}>
          Fechar
        </Button>
      </div>
      <div className="flex flex-wrap items-end gap-2 border-t border-gu-line pt-3">
        <Field
          label="Hora do jogo"
          optional
          hint="Aparece no bracket, na vista TV e no Calendário."
        >
          <DateTimeInput value={when} onChange={setWhen} />
        </Field>
        <Button
          size="sm"
          variant="secondary"
          disabled={busy || whenIso === null}
          onClick={() => void onSchedule(whenIso)}
        >
          Guardar hora
        </Button>
        {match.scheduledAt !== null ? (
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => void onSchedule(null)}>
            Tirar hora
          </Button>
        ) : null}
      </div>
      <p className="text-caption text-gu-ink-muted">
        Para corrigir um jogo cujo vencedor já jogou a ronda seguinte, apague primeiro o resultado
        desse jogo seguinte.
      </p>
    </section>
  );
}
